import React, { useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, TextInput as RNTextInput, View } from 'react-native';

import Text from '../Text';
import { Feather } from '../Icons';
import { useStyles } from '~/hooks';
import { FONT_SIZE_M, FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    // Translated via the core Text component (i18n key or plain text).
    label: string;
    value: number;
    onChange: (value: number) => void;
    min: number;
    max: number;
    step: number;
    // Decimal places the value is displayed and rounded to.
    precision: number;
    // Optional range hint under the field, e.g. "2 – 400". Rendered untranslated.
    hint?: string;
    // Optional helper line (est. profit/loss, max-from-fund…). Strings render untranslated.
    helper?: React.ReactNode;
    error?: string | null;
    disabled?: boolean;
};

// Repeat starts only after a real hold — a normal tap (~80–150ms of contact) must apply
// exactly one step via onPress, never the interval.
const LONG_PRESS_DELAY_MS = 350;
const LONG_PRESS_INTERVAL_MS = 50;

const clampAndRound = (value: number, min: number, max: number, precision: number): number => {
    const clamped = Math.min(max, Math.max(min, value));
    return Number(clamped.toFixed(precision));
};

// Keep only digits and a decimal point (commas count as points).
const sanitizeNumericText = (text: string): string => {
    let out = '';
    let seenPoint = false;
    for (const ch of text.replace(/,/g, '.')) {
        if (ch >= '0' && ch <= '9') {
            out += ch;
        } else if (ch === '.' && !seenPoint) {
            seenPoint = true;
            out += ch;
        }
    }
    return out;
};

/**
 * Compact numeric input for bounded values: − / + steppers with accelerating long-press
 * repeat, tap-to-type direct entry (committed on blur/submit, clamped to min/max and rounded
 * to the field's precision), a range hint and an optional helper/error line.
 */
export default function NumericField({
    label,
    value,
    onChange,
    min,
    max,
    step,
    precision,
    hint,
    helper,
    error,
    disabled,
}: Props) {
    const styles = useStyles(stylesGenerator);
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState('');
    const [longPressedSign, setLongPressedSign] = useState<'+' | '-'>();
    const repeatTimer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
    // True once the hold-repeat has applied at least one step; the release's onPress is
    // then swallowed so a hold doesn't end with a bonus increment.
    const repeatFiredRef = useRef(false);
    // The latest value for the long-press interval (state updates don't re-arm the timer).
    const valueRef = useRef(value);
    valueRef.current = value;

    const commitValue = (next: number) => {
        onChange(clampAndRound(next, min, max, precision));
    };

    // Accelerating repeat while a stepper button is held: after a hold delay, each tick
    // applies a growing multiple of `step`. Quick taps never reach the interval.
    useEffect(() => {
        if (repeatTimer.current) {
            clearInterval(repeatTimer.current);
        }
        if (!longPressedSign) {
            return;
        }
        const signedStep = longPressedSign === '+' ? step : -step;
        let count = 1;
        const delayHandle = setTimeout(() => {
            repeatTimer.current = setInterval(() => {
                repeatFiredRef.current = true;
                onChange(clampAndRound(valueRef.current + signedStep * count++, min, max, precision));
            }, LONG_PRESS_INTERVAL_MS);
        }, LONG_PRESS_DELAY_MS);
        return () => {
            clearTimeout(delayHandle);
            if (repeatTimer.current) {
                clearInterval(repeatTimer.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [longPressedSign, step, min, max, precision]);

    const startEditing = () => {
        if (disabled) {
            return;
        }
        setText(value.toFixed(precision));
        setEditing(true);
    };

    const commitText = () => {
        setEditing(false);
        if (text !== '' && Number.isFinite(Number(text))) {
            commitValue(Number(text));
        }
    };

    const stepperProps = (sign: '+' | '-') => ({
        disabled,
        onPress: () => {
            if (repeatFiredRef.current) {
                return;
            }
            commitValue(value + (sign === '+' ? step : -step));
        },
        onPressIn: () => {
            repeatFiredRef.current = false;
            setLongPressedSign(sign);
        },
        onPressOut: () => setLongPressedSign(undefined),
    });

    return (
        <View style={styles.container}>
            <View style={styles.labelRow}>
                <Text style={styles.label}>{label}</Text>
                {hint ? (
                    <Text noTranslate style={styles.hint}>
                        {hint}
                    </Text>
                ) : null}
            </View>
            <View style={[styles.fieldRow, error ? styles.fieldRowError : null, disabled ? styles.disabled : null]}>
                <Pressable
                    {...stepperProps('-')}
                    style={({ pressed }) => [styles.stepButton, pressed && styles.stepButtonPressed]}
                    accessibilityLabel="Decrease"
                >
                    <Feather name="minus" size={scaleSize(16)} style={styles.stepIcon} />
                </Pressable>
                {editing ? (
                    <RNTextInput
                        style={styles.input}
                        value={text}
                        onChangeText={(t) => setText(sanitizeNumericText(t))}
                        keyboardType="decimal-pad"
                        autoFocus
                        maxLength={12}
                        onBlur={commitText}
                        onSubmitEditing={commitText}
                    />
                ) : (
                    <Pressable style={styles.valuePressable} onPress={startEditing} accessibilityLabel={label}>
                        <Text noTranslate fontType="LATO_BOLD" style={styles.valueText}>
                            {value.toFixed(precision)}
                        </Text>
                    </Pressable>
                )}
                <Pressable
                    {...stepperProps('+')}
                    style={({ pressed }) => [styles.stepButton, pressed && styles.stepButtonPressed]}
                    accessibilityLabel="Increase"
                >
                    <Feather name="plus" size={scaleSize(16)} style={styles.stepIcon} />
                </Pressable>
            </View>
            {error ? (
                <Text noTranslate style={styles.errorText}>
                    {error}
                </Text>
            ) : typeof helper === 'string' ? (
                <Text noTranslate style={styles.helperText}>
                    {helper}
                </Text>
            ) : (
                helper ?? null
            )}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            marginBottom: scaleSize(14),
        },
        labelRow: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: scaleSize(6),
        },
        label: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
        },
        hint: {
            fontSize: FONT_SIZE_XS,
            color: theme.textSecondary,
        },
        fieldRow: {
            flexDirection: 'row',
            alignItems: 'center',
            borderWidth: 1,
            borderColor: theme.equityValue,
            borderRadius: scaleSize(8),
            backgroundColor: theme.backgroundSecondary,
        },
        fieldRowError: {
            borderColor: theme.alert,
        },
        disabled: {
            opacity: 0.5,
        },
        stepButton: {
            paddingHorizontal: scaleSize(14),
            paddingVertical: scaleSize(10),
        },
        stepButtonPressed: {
            opacity: 0.5,
        },
        stepIcon: {
            color: theme.text,
        },
        valuePressable: {
            flex: 1,
            alignItems: 'center',
        },
        valueText: {
            fontSize: FONT_SIZE_M,
            color: theme.text,
        },
        input: {
            flex: 1,
            textAlign: 'center',
            fontSize: FONT_SIZE_M,
            color: theme.text,
            paddingVertical: 0,
        },
        helperText: {
            fontSize: FONT_SIZE_XS,
            color: theme.textSecondary,
            marginTop: scaleSize(4),
        },
        errorText: {
            fontSize: FONT_SIZE_XS,
            color: theme.alert,
            marginTop: scaleSize(4),
        },
    });
